import React, { type ReactNode, useEffect, useMemo } from 'react'
import { useCollectionData } from 'react-firebase-hooks/firestore'

import { captureException } from '@sentry/react'
import { query, where } from 'firebase/firestore'
import { compact, map, uniq } from 'lodash-es'

import { teamCollection, type TeamSnapshotData } from '../firestore/teamCollection.ts'

export type Team = TeamSnapshotData & {
  readonly isAvailable: boolean
}

export interface TeamsContextValue {
  readonly loading: boolean
  readonly teams: ReadonlyArray<Team>
  readonly bases: string[]
  readonly startingLocations: string[]
}

export const TeamsContext = React.createContext<TeamsContextValue>({
  teams: [],
  bases: [],
  startingLocations: [],
  loading: true,
})

export interface TeamsProviderProps {
  regionId: string
  eveningId: number
  children: ReactNode
}

export const TeamsProvider: React.FC<TeamsProviderProps> = ({ regionId, eveningId, children }) => {
  const teamsQuery = query(teamCollection(), where('regionId', '==', regionId), where('eveningId', '==', eveningId))
  const [teams = [], loading, error] = useCollectionData<TeamSnapshotData>(teamsQuery)
  useEffect(() => {
    if (error) {
      captureException(error, {
        tags: { 'firestore.query.snapshot': 'regions' },
        fingerprint: ['firestore', 'query', 'teams'],
      })
    }
  }, [error])

  const value = useMemo(
    () => ({
      teams: teams.map((team) => ({
        ...team,
        isAvailable: [
          'AVAILABLE_IN_LOCATION',
          'AVAILABLE_IN_INSTITUTION',
          'AVAILABLE_IN_REGION',
          'AVAILABLE_AT_COORDINATES',
        ].includes(team.status),
      })),
      loading,
      bases: uniq(compact(map(teams, 'base'))),
      startingLocations: uniq(compact(map(teams, 'startingLocation'))),
    }),
    [teams, loading]
  )
  return <TeamsContext.Provider value={value}>{children}</TeamsContext.Provider>
}
